import { useState, useRef, useEffect, useCallback } from "react";
import html2pdf from "html2pdf.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// Engine
import { normalizeResidency, calcMonth, NON_RESIDENT_BRACKETS, RESIDENT_BRACKETS } from "./engine/taxEngine";
import { buildPayslipHTML } from "./engine/payslipPNG";
import { buildSingaporePayslipHTML } from "./engine/payslipSG";

// Hooks
import { useFirebase } from "./hooks/useFirebase";

// Constants & Styles
import { MONTHS, MONTHS_SHORT, CURRENCIES, LOCAL_STORAGE_KEY, DEFAULT_ADMINS, AUTHORIZED_EMAILS, now, emptyLine, emptyAllowance, newEmployee } from "./constants";
import { S, fmt, pct } from "./styles";

// Components
import { Field, Pill } from "./components/Field";
import { DriveStatusBar } from "./components/DriveStatusBar";
import { AccessDenied } from "./components/AccessDenied";
import { UserAdminPanel } from "./components/UserAdminPanel";

// ─── Month Editor Modal ────────────────────────────────────────────────────────
function MonthEditor({ employee, monthKey, onSave, onClose, ytdGross, ytdTax, fxRates }) {
  const ex = employee.monthlyData?.[monthKey] || {};
  const currency = employee.currency || "PGK";
  const [lines, setLines] = useState(ex.rateLines?.length ? ex.rateLines : [emptyLine(1), emptyLine(2), emptyLine(3)]);
  const [allowances, setAllowances] = useState(ex.allowances?.length ? ex.allowances : [emptyAllowance(1)]);

  // FX rate: prefer master table, fallback to saved value, fallback to 1
  const masterRate = currency !== "PGK" ? (fxRates?.[monthKey]?.[currency] ?? "") : "";
  const [localFxRate, setLocalFxRate] = useState(ex.fxRate ? String(ex.fxRate) : "");
  // Effective rate used: master table takes priority, then local override
  const effectiveFxRate = masterRate !== "" ? masterRate : localFxRate;

  const [monthSplit, setMonthSplit] = useState(ex.monthSplit !== undefined ? ex.monthSplit : null); // null = inherit from employee
  const isSplit = monthSplit !== null ? monthSplit : (employee.splitContract || false);
  const [taxExempt, setTaxExempt] = useState(ex.taxExempt || false);
  const [result, setResult] = useState(ex.result || null);
  const [showPayslip, setShowPayslip] = useState(false);   // false | "png" | "sg"
  const iframeRef = useRef(null);
  const [year, monthNum] = monthKey.split("-");
  const monthName = MONTHS[parseInt(monthNum) - 1];

  const setLine = (id, f, v) => { setLines(p => p.map(l => l.id===id ? {...l,[f]:v} : l)); setResult(null); };
  const setAllo = (id, f, v) => { setAllowances(p => p.map(a => a.id===id ? {...a,[f]:v} : a)); setResult(null); };

  // Convert foreign-currency rates to PGK for tax engine
  const pgkLines = currency === "PGK"
    ? lines
    : lines.map(l => ({ ...l, rate: l.rate !== "" ? String(parseFloat(l.rate) * parseFloat(effectiveFxRate || 1)) : "" }));

  // Split allowances by entity and convert to PGK for tax engine
  const pngAllowancesRaw = isSplit ? allowances.filter(a => (a.entity || "png") === "png") : allowances;
  const sgAllowances = isSplit ? allowances.filter(a => a.entity === "sg") : [];
  const fxMul = parseFloat(effectiveFxRate || 1);
  const pngAllowances = currency === "PGK"
    ? pngAllowancesRaw
    : pngAllowancesRaw.map(a => ({ ...a, amount: a.amount !== "" ? String(parseFloat(a.amount) * fxMul) : "" }));

  const calculate = () => {
    if (currency !== "PGK" && !parseFloat(effectiveFxRate)) {
      alert(`Please set the ${currency}/PGK exchange rate for ${monthName} ${year} in the FX Rates table, or enter it below.`);
      return;
    }
    const res = calcMonth(pgkLines, pngAllowances, employee.empType, employee.dependants);
    if (!res) { alert("Please fill in at least one rate line with a rate (and days if daily)."); return; }
    const fxVal = parseFloat(effectiveFxRate || 1);

    if (taxExempt) {
      // Tax exempt: gross = net, no tax
      res.monthlyGross = res.totalNet;
      res.monthlyTax = 0;
      res.annualGross = res.totalDays > 0 ? res.totalNet * (22 / res.totalDays) * 12 : res.totalNet * 12;
      res.annualTax = 0;
      res.rebate = 0;
      res.effectiveRate = 0;
      res.taxExempt = true;
    } else {
      res.taxExempt = false;
    }

    const fxNet = currency !== "PGK" ? res.totalNet / fxVal : null;

    // Singapore portion (split contract)
    let sgTotalNative = 0, sgTotalPGK = 0, sgAllowTotal = 0;
    if (isSplit) {
      for (const l of lines) {
        const days = parseFloat(l.days) || 0;
        const sgRate = parseFloat(l.sgRate) || 0;
        const isMonthly = l.rateMode === "monthly";
        if (sgRate > 0 && (isMonthly || days > 0)) {
          sgTotalNative += isMonthly ? sgRate : days * sgRate;
        }
      }
      sgTotalPGK = currency !== "PGK" ? sgTotalNative * fxVal : sgTotalNative;
      // SG allowances/expenses total
      sgAllowTotal = sgAllowances.reduce((s, a) => {
        const amt = parseFloat(a.amount) || 0;
        return s + (a.type === "per_diem" ? amt * res.totalDays : amt);
      }, 0);
    }

    setResult({ ...res, fxNet, fxRate: fxVal, currency, sgTotalNative, sgTotalPGK, sgAllowTotal, isSplit });
  };

  const save = () => {
    onSave(monthKey, { rateLines: lines, allowances, fxRate: parseFloat(effectiveFxRate || 1), taxExempt, monthSplit, result });
    onClose();
  };

  const printIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const [yr, mo] = monthKey.split("-");
    const moName = MONTHS[parseInt(mo) - 1];
    const empName = (employee.name || "Employee").replace(/\s+/g, "_");
    const prefix = showPayslip === "sg" ? "payslip_SG" : "payslip";
    const savedTitle = document.title;
    document.title = `${prefix}_${empName}_${moName}_${yr}`;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    document.title = savedTitle;
  };

  const payslipHTML = result
    ? buildPayslipHTML(employee, monthKey, { rateLines: lines, allowances, result }, ytdGross, ytdTax)
    : "";
  // Compute YTD SG earnings (prior months)
  const ytdSgEarnings = (() => {
    if (!isSplit) return 0;
    const monthIdx = parseInt(monthKey.split("-")[1]) - 1;
    let total = 0;
    for (let m = 0; m < monthIdx; m++) {
      const k = `${year}-${String(m + 1).padStart(2, "0")}`;
      const md = employee.monthlyData?.[k];
      if (md?.result?.sgTotalPGK) total += md.result.sgTotalPGK;
    }
    return total;
  })();

  const sgPayslipHTML = result && isSplit
    ? buildSingaporePayslipHTML(employee, monthKey, { rateLines: lines, allowances, result }, ytdSgEarnings)
    : "";

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:100, overflowY:"auto", display:"flex", justifyContent:"center", padding:"30px 16px" }}>
      <div style={{ background:"#2a3040", border:"1px solid rgba(255,197,67,0.3)", borderRadius:14, padding:26, width:"100%", maxWidth:780, alignSelf:"flex-start" }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div>
            <div style={{ color:"#ffc543", fontWeight:700, fontSize:17 }}>{monthName} {year}</div>
            <div style={{ color:"#75788b", fontSize:12, marginTop:2 }}>{employee.name || "Unnamed"} · {normalizeResidency(employee.empType) === "resident" ? "Resident" : "Non-Resident"}</div>
          </div>
          <button style={S.btnRed} onClick={onClose}>✕ Close</button>
        </div>

        {/* Per-month split toggle */}
        <div style={{ marginBottom:16, padding:"10px 14px", background: isSplit ? "rgba(246,134,68,0.08)" : "rgba(255,255,255,0.03)", border:`1px solid ${isSplit ? "rgba(246,134,68,0.25)" : "rgba(255,255,255,0.08)"}`, borderRadius:8 }}>
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
            <input type="checkbox" checked={isSplit} onChange={e => { setMonthSplit(e.target.checked === (employee.splitContract || false) ? null : e.target.checked); setResult(null); }} style={{ accentColor:"#f68644", width:16, height:16 }} />
            <span style={{ color: isSplit ? "#f68644" : "#75788b", fontWeight:600, fontSize:13 }}>Split Contract (PNG + Singapore)</span>
            {monthSplit !== null && <span style={{ color:"#f68644", fontSize:10, background:"rgba(246,134,68,0.15)", borderRadius:4, padding:"2px 8px", marginLeft:4 }}>month override</span>}
          </label>
        </div>

        {/* Rate lines */}
        <div style={{ marginBottom:20 }}>
          <div style={{ color:"#31b4a6", fontSize:10, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", borderBottom:"1px solid rgba(49,180,166,0.2)", paddingBottom:5, marginBottom:10 }}>
            Taxable Earnings — Daily Rates
          </div>

          {/* FX Rate input — only shown for non-PGK employees */}
          {currency !== "PGK" && (
            <div style={{ marginBottom:14, padding:"10px 14px", background: masterRate ? "rgba(49,180,166,0.07)" : "rgba(255,197,67,0.07)", border:`1px solid ${masterRate ? "rgba(49,180,166,0.3)" : "rgba(255,197,67,0.25)"}`, borderRadius:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                <div style={{ color: masterRate ? "#31b4a6" : "#c29a52", fontSize:12, fontWeight:700 }}>
                  🔄 FX Rate — {monthName} {year}
                </div>
                {masterRate ? (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ color:"#31b4a6", fontFamily:"'Poppins',sans-serif", fontSize:14, fontWeight:700 }}>1 {currency} = {masterRate} PGK</span>
                    <span style={{ color:"#75788b", fontSize:10, background:"rgba(49,180,166,0.1)", borderRadius:4, padding:"2px 6px" }}>from master table ✓</span>
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ color:"#75788b", fontSize:11 }}>Not set in master table — enter manually:</span>
                    <span style={{ color:"#75788b", fontSize:12 }}>1 {currency} =</span>
                    <input
                      style={{ ...S.input, width:90, textAlign:"right" }}
                      type="number" min={0} step="0.0001" placeholder="0.0000"
                      value={localFxRate}
                      onChange={e => { setLocalFxRate(e.target.value); setResult(null); }}
                    />
                    <span style={{ color:"#75788b", fontSize:12 }}>PGK</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {isSplit && (
            <div style={{ marginBottom:10, padding:"8px 12px", background:"rgba(69,188,203,0.08)", border:"1px solid rgba(69,188,203,0.25)", borderRadius:8 }}>
              <span style={{ color:"#45bccb", fontSize:11, fontWeight:600 }}>Split Contract — </span>
              <span style={{ color:"#75788b", fontSize:11 }}>PNG rate = taxed locally · SG rate = paid offshore (no tax)</span>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns: isSplit ? "130px 80px 60px 1fr 1fr 100px 36px" : "160px 80px 80px 1fr 110px 36px", gap:"6px 10px", marginBottom:6 }}>
            {(isSplit
              ? ["Rate Type","Mode","Days",`PNG Rate (${currency})`,`SG Rate (${currency})`,"Subtotal",""]
              : ["Rate Type","Mode","Days",`Net Rate (${currency})`,"Subtotal (PGK)",""]
            ).map((h,i) => <div key={i} style={S.label}>{h}</div>)}
          </div>
          {lines.map(l => {
            const isMonthly = l.rateMode === "monthly";
            const pgkRate = currency !== "PGK" && l.rate !== "" ? parseFloat(l.rate) * parseFloat(effectiveFxRate || 1) : null;
            const days = parseFloat(l.days) || 0;
            const pngAmt = parseFloat(l.rate) || 0;
            const sgAmt = parseFloat(l.sgRate) || 0;
            const pngTotal = isMonthly ? pngAmt : days * pngAmt;
            const pgkTotal = pgkRate !== null ? (isMonthly ? pgkRate : days * pgkRate) : pngTotal;
            const hasValue = l.rate && (isMonthly || l.days);
            return (
            <div key={l.id} style={{ display:"grid", gridTemplateColumns: isSplit ? "130px 80px 60px 1fr 1fr 100px 36px" : "160px 80px 80px 1fr 110px 36px", gap:"6px 10px", marginBottom:7, alignItems:"center" }}>
              <select style={S.select} value={l.label} onChange={e => setLine(l.id,"label",e.target.value)}>
                <option>Office</option><option>Field</option><option>Business Travel</option><option>Other</option>
              </select>
              <select style={S.select} value={l.rateMode || "daily"} onChange={e => setLine(l.id,"rateMode",e.target.value)}>
                <option value="daily">Daily</option><option value="monthly">Monthly</option>
              </select>
              {isMonthly
                ? <div style={{ color:"#384460", fontSize:10, textAlign:"center" }}>—</div>
                : <input style={S.input} type="number" min={0} placeholder="0" value={l.days} onChange={e => setLine(l.id,"days",e.target.value)} />}
              <input style={S.input} type="number" min={0} placeholder="0.00" value={l.rate} onChange={e => setLine(l.id,"rate",e.target.value)} />
              {isSplit && <input style={{ ...S.input, borderColor:"rgba(69,188,203,0.3)" }} type="number" min={0} placeholder="0.00" value={l.sgRate||""} onChange={e => setLine(l.id,"sgRate",e.target.value)} />}
              <div style={{ fontFamily:"'Poppins',sans-serif", fontSize:10 }}>
                {hasValue
                  ? <div>
                      {currency !== "PGK" && <div style={{ color:"#75788b", fontSize:9 }}>{currency} {fmt(pngTotal)}</div>}
                      <div style={{ color:"#ffc543" }}>PGK {fmt(pgkTotal)}</div>
                      {isSplit && sgAmt > 0 && <div style={{ color:"#45bccb", fontSize:9, marginTop:1 }}>SG: {currency} {fmt(isMonthly ? sgAmt : days*sgAmt)}</div>}
                    </div>
                  : <span style={{color:"#384460"}}>—</span>}
              </div>
              {lines.length > 1
                ? <button style={S.btnRed} onClick={() => setLines(p => p.filter(x => x.id!==l.id))}>✕</button>
                : <div/>}
            </div>
          );})}
          <button style={{...S.btnGhost, fontSize:12, padding:"6px 12px", marginTop:4}} onClick={() => setLines(p => [...p, emptyLine(Date.now())])}>+ Add Rate Line</button>
        </div>

        {/* Allowances & Expenses */}
        <div style={{ marginBottom:22 }}>
          <div style={{ color:"#31b4a6", fontSize:10, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", borderBottom:"1px solid rgba(49,180,166,0.2)", paddingBottom:5, marginBottom:10 }}>
            Allowances &amp; Expenses (Tax Exempt)
          </div>
          <div style={{ display:"grid", gridTemplateColumns: isSplit ? "1fr 120px 70px 80px 90px 36px" : "1fr 140px 70px 100px 36px", gap:"6px 10px", marginBottom:6 }}>
            {(isSplit
              ? ["Description","Type","Ccy","Amount","Paid by",""]
              : ["Description","Type","Ccy","Amount",""]
            ).map((h,i) => <div key={i} style={S.label}>{h}</div>)}
          </div>
          {allowances.map(a => {
            const alloCcy = a.currency || currency;
            return (
            <div key={a.id} style={{ display:"grid", gridTemplateColumns: isSplit ? "1fr 120px 70px 80px 90px 36px" : "1fr 140px 70px 100px 36px", gap:"6px 10px", marginBottom:7, alignItems:"center" }}>
              <input style={S.input} placeholder="e.g. Housing, Per Diem, Expense…" value={a.label} onChange={e => setAllo(a.id,"label",e.target.value)} />
              <select style={S.select} value={a.type} onChange={e => setAllo(a.id,"type",e.target.value)}>
                <option value="fixed_monthly">Fixed (monthly)</option>
                <option value="per_diem">Per Diem (× days)</option>
                <option value="expense">Expense</option>
              </select>
              <select style={{ ...S.select, fontSize:11, padding:"7px 4px" }} value={alloCcy} onChange={e => setAllo(a.id,"currency",e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input style={S.input} type="number" min={0} placeholder="0.00" value={a.amount} onChange={e => setAllo(a.id,"amount",e.target.value)} />
              {isSplit && (
                <select style={{ ...S.select, fontSize:11, borderColor: (a.entity || "png") === "sg" ? "rgba(69,188,203,0.3)" : undefined }} value={a.entity || "png"} onChange={e => setAllo(a.id,"entity",e.target.value)}>
                  <option value="png">PNG</option>
                  <option value="sg">Singapore</option>
                </select>
              )}
              {allowances.length > 1
                ? <button style={S.btnRed} onClick={() => setAllowances(p => p.filter(x => x.id!==a.id))}>✕</button>
                : <div/>}
            </div>
          );
          })}
          <button style={{...S.btnGhost, fontSize:12, padding:"6px 12px", marginTop:4}} onClick={() => setAllowances(p => [...p, emptyAllowance(Date.now())])}>+ Add Allowance / Expense</button>
        </div>

        {/* Tax Exempt toggle */}
        <div style={{ marginBottom:16, padding:"10px 14px", background: taxExempt ? "rgba(246,134,68,0.1)" : "rgba(255,255,255,0.03)", border:`1px solid ${taxExempt ? "rgba(246,134,68,0.35)" : "rgba(255,255,255,0.08)"}`, borderRadius:8 }}>
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
            <input type="checkbox" checked={taxExempt} onChange={e => { setTaxExempt(e.target.checked); setResult(null); }} style={{ accentColor:"#f68644", width:16, height:16 }} />
            <span style={{ color: taxExempt ? "#f68644" : "#75788b", fontWeight:600, fontSize:13 }}>Tax Exempt this month</span>
            {taxExempt && <span style={{ color:"#f68644", fontSize:10, background:"rgba(246,134,68,0.15)", borderRadius:4, padding:"2px 8px", marginLeft:4 }}>No tax will be applied</span>}
          </label>
          {taxExempt && <div style={{ color:"#75788b", fontSize:10, marginTop:4, marginLeft:26 }}>For internationally-funded projects exempt from PNG salary tax</div>}
        </div>

        {/* Actions */}
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <button style={S.btnYellow} onClick={calculate}>⚡ Calculate</button>
          {result && <button style={S.btnGhost} onClick={save}>💾 Save</button>}
          {result && <button style={S.btnTeal} onClick={() => setShowPayslip("png")}>📄 Preview Payslip{isSplit ? " (PNG)" : ""}</button>}
          {result && isSplit && <button style={{ ...S.btnGhost, borderColor:"rgba(69,188,203,0.3)", color:"#45bccb" }} onClick={() => setShowPayslip("sg")}>📄 Preview Payslip (SG)</button>}
        </div>

        {/* Payslip preview modal */}
        {showPayslip && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:200, display:"flex", flexDirection:"column" }}>
            {/* Toolbar */}
            <div style={{ background:"#2a3040", borderBottom:`1px solid ${showPayslip === "sg" ? "rgba(69,188,203,0.3)" : "rgba(255,197,67,0.3)"}`, padding:"12px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <span style={{ color: showPayslip === "sg" ? "#45bccb" : "#ffc543", fontWeight:700, fontSize:14 }}>
                  📄 {showPayslip === "sg" ? "Singapore Payslip" : "PNG Payslip"}
                </span>
                <span style={{ color:"#75788b", fontSize:12 }}>{employee.name} · {monthName} {year}</span>
                {isSplit && (
                  <div style={{ display:"flex", gap:4 }}>
                    <button onClick={() => setShowPayslip("png")} style={{ ...S.btnGhost, padding:"4px 10px", fontSize:11, background: showPayslip === "png" ? "rgba(255,197,67,0.15)" : undefined, color: showPayslip === "png" ? "#ffc543" : "#75788b" }}>PNG</button>
                    <button onClick={() => setShowPayslip("sg")} style={{ ...S.btnGhost, padding:"4px 10px", fontSize:11, background: showPayslip === "sg" ? "rgba(69,188,203,0.15)" : undefined, color: showPayslip === "sg" ? "#45bccb" : "#75788b" }}>Singapore</button>
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button style={S.btnTeal} onClick={printIframe}>🖨 Print / Save as PDF</button>
                <button style={S.btnRed}  onClick={() => setShowPayslip(false)}>✕ Close Preview</button>
              </div>
            </div>
            {/* Iframe showing payslip */}
            <div style={{ flex:1, background:"#555", overflow:"hidden", display:"flex", justifyContent:"center", alignItems:"flex-start", padding:"24px 0", overflowY:"auto" }}>
              <iframe
                ref={iframeRef}
                srcDoc={showPayslip === "sg" ? sgPayslipHTML : payslipHTML}
                style={{ width:"210mm", minHeight:"297mm", border:"none", boxShadow:"0 8px 40px rgba(0,0,0,0.6)", background:"#fff" }}
                title="Payslip Preview"
              />
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div style={{ marginTop:20, borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:18 }}>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
              <Pill label="Total Days"     value={fmt(result.totalDays,0)} />
              <Pill label="Net Earned (PGK)" value={`PGK ${fmt(result.totalNet)}`} color="teal" />
              {result.fxNet != null && <Pill label={`Net Earned (${result.currency})`} value={`${result.currency} ${fmt(result.fxNet)}`} color="teal" />}
              <Pill label="Monthly Gross"  value={`PGK ${fmt(result.monthlyGross)}`} />
              <Pill label="Monthly Tax"    value={`PGK ${fmt(result.monthlyTax)}`}    color="gold" />
              {result.totalAllowances > 0 && <Pill label="Allowances" value={`PGK ${fmt(result.totalAllowances)}`} />}
              <Pill label="Eff. Rate"      value={pct(result.effectiveRate)} />
              {result.isSplit && result.sgTotalNative > 0 && (
                <Pill label={`SG Portion (${result.currency || "PGK"})`} value={`${result.currency || "PGK"} ${fmt(result.sgTotalNative)}`} color="teal" />
              )}
              {result.isSplit && result.sgTotalPGK > 0 && result.currency !== "PGK" && (
                <Pill label="SG Portion (PGK)" value={`PGK ${fmt(result.sgTotalPGK)}`} />
              )}
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <Pill label="YTD Gross (this month incl.)" value={`PGK ${fmt(ytdGross + result.monthlyGross)}`} />
              <Pill label="YTD Tax (this month incl.)"   value={`PGK ${fmt(ytdTax + result.monthlyTax)}`} color="gold" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Employee Card ─────────────────────────────────────────────────────────────
function EmployeeCard({ employee, onUpdate, onDelete, onArchive, fxRates }) {
  const [expanded, setExpanded] = useState(false);
  const [editMonth, setEditMonth] = useState(null);
  const year = employee.year || now.getFullYear();
  const currency = employee.currency || "PGK";

  const ytdUpTo = (monthIdx) => {
    let gross = 0, tax = 0;
    for (let m = 0; m < monthIdx; m++) {
      const key = `${year}-${String(m+1).padStart(2,"0")}`;
      const d = employee.monthlyData?.[key];
      if (d?.result) { gross += d.result.monthlyGross||0; tax += d.result.monthlyTax||0; }
    }
    return { gross, tax };
  };

  const total = ytdUpTo(12);

  return (
    <div style={{...S.card, marginBottom:12, opacity: employee.archived ? 0.6 : 1, borderColor: employee.archived ? "rgba(246,134,68,0.2)" : undefined }}>
      <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ fontWeight:700, fontSize:15, color:"#ffffff", display:"flex", alignItems:"center", gap:8 }}>
            {employee.name || <span style={{color:"#384460"}}>Unnamed Employee</span>}
            {employee.archived && <span style={{ fontSize:9, background:"rgba(246,134,68,0.15)", color:"#f68644", border:"1px solid rgba(246,134,68,0.3)", borderRadius:4, padding:"2px 6px", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase" }}>Archived</span>}
          </div>
          <div style={{ fontSize:11, color:"#75788b", marginTop:2 }}>
            <span style={{ color: normalizeResidency(employee.empType)==="non-resident" ? "#82a8d4":"#82d4a8", fontWeight:600 }}>
              {normalizeResidency(employee.empType)==="resident" ? "Resident":"Non-Resident"}
            </span>
            {employee.position && ` · ${employee.position}`}
            {employee.project  && ` · ${employee.project}`}
            {employee.currency && employee.currency !== "PGK" && (
              <span style={{ marginLeft:6, background:"rgba(255,197,67,0.15)", border:"1px solid rgba(255,197,67,0.3)", color:"#ffc543", borderRadius:4, padding:"1px 6px", fontSize:10, fontWeight:700 }}>{employee.currency}</span>
            )}
          </div>
        </div>
        {total.gross > 0 && (
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:10, color:"#384460", textTransform:"uppercase", letterSpacing:"0.08em" }}>YTD {year}</div>
            <div style={{ fontFamily:"'Poppins',sans-serif", color:"#ffc543", fontSize:12 }}>Gross PGK {fmt(total.gross)}</div>
            <div style={{ fontFamily:"'Poppins',sans-serif", color:"#f68644", fontSize:11 }}>Tax&nbsp;&nbsp; PGK {fmt(total.tax)}</div>
          </div>
        )}
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.btnGhost} onClick={() => setExpanded(e=>!e)}>{expanded?"▲":"▼"} {expanded?"Collapse":"Expand"}</button>
          <button style={{ ...S.btnGhost, color: employee.archived ? "#31b4a6" : "#f68644", borderColor: employee.archived ? "rgba(49,180,166,0.3)" : "rgba(246,134,68,0.3)" }} onClick={onArchive}>
            {employee.archived ? "Unarchive" : "Archive"}
          </button>
          <button style={S.btnRed} onClick={() => onDelete(employee.id)}>Delete</button>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop:18, borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:18 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12, marginBottom:20 }}>
            <Field label="Full Name">   <input style={S.input} value={employee.name}      onChange={e => onUpdate({...employee, name:e.target.value})} /></Field>
            <Field label="Matricule">  <input style={S.input} placeholder="e.g. EMP-001" value={employee.matricule||""} onChange={e => onUpdate({...employee, matricule:e.target.value})} /></Field>
            <Field label="Position">   <input style={S.input} value={employee.position||""} onChange={e => onUpdate({...employee, position:e.target.value})} /></Field>
            <Field label="Address">    <input style={S.input} value={employee.address||""} onChange={e => onUpdate({...employee, address:e.target.value})} /></Field>
            <Field label="Project">    <input style={S.input} value={employee.project||""} onChange={e => onUpdate({...employee, project:e.target.value})} /></Field>
            <Field label="Tax Residency">
              <select style={S.select} value={normalizeResidency(employee.empType)} onChange={e => onUpdate({...employee, empType:e.target.value})}>
                <option value="resident">Resident</option>
                <option value="non-resident">Non-Resident</option>
              </select>
            </Field>
            <Field label="Pay Currency">
              <select style={S.select} value={employee.currency || "PGK"} onChange={e => onUpdate({...employee, currency:e.target.value})}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Dependants (max 3)"><input style={S.input} type="number" min={0} max={3} value={employee.dependants} onChange={e => onUpdate({...employee, dependants:Math.min(3, Math.max(0, parseInt(e.target.value)||0))})} /></Field>
            <Field label="Split Contract">
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <input type="checkbox" checked={employee.splitContract || false} onChange={e => onUpdate({...employee, splitContract:e.target.checked})} style={{ accentColor:"#45bccb", width:16, height:16 }} />
                <span style={{ color: employee.splitContract ? "#45bccb" : "#75788b", fontSize:12, fontWeight:600 }}>
                  {employee.splitContract ? "PNG + Singapore" : "PNG only"}
                </span>
              </label>
            </Field>
            <Field label="Start Date"><input style={S.input} type="date" value={employee.startDate||""} onChange={e => onUpdate({...employee, startDate:e.target.value})} /></Field>
            <Field label="End Date"><input style={S.input} type="date" value={employee.endDate||""} onChange={e => onUpdate({...employee, endDate:e.target.value})} /></Field>
            <Field label="Payroll Year"><input style={S.input} type="number" value={year} onChange={e => onUpdate({...employee, year:parseInt(e.target.value)})} /></Field>
          </div>

          <div style={{ fontSize:10, fontWeight:700, color:"#75788b", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:10 }}>
            Monthly Payroll — Click a month to enter data
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8 }}>
            {MONTHS_SHORT.map((m, i) => {
              const key = `${year}-${String(i+1).padStart(2,"0")}`;
              const d = employee.monthlyData?.[key];
              const done = !!d?.result;
              const monthIsSplit = d?.monthSplit !== undefined && d?.monthSplit !== null ? d.monthSplit : (employee.splitContract || false);
              const split = done && (d.result.isSplit || monthIsSplit);
              const cellColor = split ? "#f68644" : "#31b4a6";
              return (
                <button key={m} onClick={() => setEditMonth(key)} style={{
                  background: done ? (split ? "rgba(246,134,68,0.1)" : "rgba(49,180,166,0.1)") : "rgba(255,255,255,0.03)",
                  border: done ? `1px solid ${split ? "rgba(246,134,68,0.4)" : "rgba(49,180,166,0.4)"}` : "1px solid rgba(255,255,255,0.08)",
                  borderRadius:8, padding:"10px 6px", cursor:"pointer", textAlign:"center", transition:"all 0.15s",
                }}>
                  <div style={{ color: done ? cellColor : "#555", fontWeight:700, fontSize:13 }}>{m}</div>
                  {done
                    ? <div style={{ fontFamily:"'Poppins',sans-serif", fontSize:10, marginTop:3 }}>
                        <div style={{ color: cellColor }}>PGK {fmt(d.result.totalNet + (d.result.totalAllowances || 0), 0)}</div>
                        {currency !== "PGK" && d.result.fxNet != null && (
                          <div style={{ color:"#ffc543", fontSize:9 }}>{currency} {fmt(d.result.fxNet + (d.result.totalAllowances || 0) / (d.result.fxRate || 1), 0)}</div>
                        )}
                      </div>
                    : <div style={{ color:"#384460", fontSize:9, marginTop:3 }}>—</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {editMonth && (
        <MonthEditor
          employee={employee}
          monthKey={editMonth}
          onSave={(key, data) => onUpdate({...employee, monthlyData:{...(employee.monthlyData||{}), [key]:data}})}
          onClose={() => setEditMonth(null)}
          ytdGross={ytdUpTo(parseInt(editMonth.split("-")[1]) - 1).gross}
          ytdTax={ytdUpTo(parseInt(editMonth.split("-")[1]) - 1).tax}
          fxRates={fxRates}
        />
      )}
    </div>
  );
}

// ─── Excel Export ──────────────────────────────────────────────────────────────
function exportToCSV(rows, filename) {
  const headers = ["Employee","Type","Dependants","Project","Month","Year","Pay Currency","FX Rate (PGK/unit)","Days Worked","Net Rate Types","Net/day avg (currency)","Net Earned (currency)","Net Earned (PGK)","Gross Salary (PGK)","Gross Salary (currency)","Tax Withheld (PGK)","Tax Withheld (currency)","Effective Rate (%)","Allowances (PGK)","Allowances (currency)","Total Paid (PGK)","Total Paid (currency)"];
  const escape = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const csv = [headers, ...rows].map(r => r.map(escape).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function buildExportRows(employees, selectedMonthKeys, fxRates = {}) {
  const rows = [];
  for (const emp of employees) {
    for (const key of selectedMonthKeys) {
      const d = emp.monthlyData?.[key];
      if (!d?.result) continue;
      const r = d.result;
      const currency = emp.currency || "PGK";
      const fxRate = r.fxRate || parseFloat(fxRates?.[key]?.[currency]) || 1;
      const [year, monthNum] = key.split("-");
      const monthName = MONTHS_SHORT[parseInt(monthNum) - 1];
      const rateDesc = (d.rateLines || []).filter(l => parseFloat(l.rate) > 0 && (l.rateMode === "monthly" || parseFloat(l.days) > 0)).map(l => l.rateMode === "monthly" ? `${l.label}(monthly)` : `${l.label}(${l.days}d)`).join(" + ");
      const avgRatePGK = r.totalDays > 0 ? r.totalNet / r.totalDays : 0;
      const avgRateCcy = currency !== "PGK" ? avgRatePGK / fxRate : avgRatePGK;
      const netCcy = currency !== "PGK" ? (r.fxNet ?? r.totalNet / fxRate) : r.totalNet;
      const isFx = currency !== "PGK";
      const grossCcy = isFx ? (r.monthlyGross / fxRate).toFixed(2) : r.monthlyGross.toFixed(2);
      const taxCcy = isFx ? (r.monthlyTax / fxRate).toFixed(2) : r.monthlyTax.toFixed(2);
      const allowCcy = isFx ? (r.totalAllowances / fxRate).toFixed(2) : r.totalAllowances.toFixed(2);
      const totalPaid = r.totalNet + r.totalAllowances;
      const totalPaidCcy = isFx ? (totalPaid / fxRate).toFixed(2) : totalPaid.toFixed(2);
      rows.push([
        emp.name || "Unnamed",
        normalizeResidency(emp.empType) === "resident" ? "Resident" : "Non-Resident",
        normalizeResidency(emp.empType) === "resident" ? emp.dependants : "N/A",
        emp.project || "",
        monthName, year,
        currency,
        isFx ? fxRate.toFixed(4) : "1.0000",
        r.totalDays.toFixed(1),
        rateDesc,
        avgRateCcy.toFixed(2),
        netCcy.toFixed(2),
        r.totalNet.toFixed(2),
        r.monthlyGross.toFixed(2),
        grossCcy,
        r.monthlyTax.toFixed(2),
        taxCcy,
        (r.effectiveRate * 100).toFixed(2),
        r.totalAllowances.toFixed(2),
        allowCcy,
        totalPaid.toFixed(2),
        totalPaidCcy,
      ]);
    }
  }
  return rows;
}

// ─── Export Modal ──────────────────────────────────────────────────────────────
function ExportModal({ employees, fxRates, onClose }) {
  const currentYear = new Date().getFullYear();
  const [selectedEmpIds, setSelectedEmpIds] = useState(new Set(employees.map(e => String(e.id))));
  const [mode, setMode] = useState("months");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonths, setSelectedMonths] = useState(new Set());

  const allKeys = new Set();
  for (const emp of employees) {
    for (const key of Object.keys(emp.monthlyData || {})) {
      if (emp.monthlyData[key]?.result) allKeys.add(key);
    }
  }
  const sortedKeys = [...allKeys].sort();
  const yearKeys = sortedKeys.filter(k => k.startsWith(String(selectedYear)));

  const toggleMonth = (key) => {
    setSelectedMonths(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const getTargetEmps = () => employees.filter(e => selectedEmpIds.has(String(e.id)));

  const toggleEmp = (id) => {
    setSelectedEmpIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllEmps = () => {
    setSelectedEmpIds(prev => prev.size === employees.length ? new Set() : new Set(employees.map(e => String(e.id))));
  };

  const doExport = () => {
    const targetEmps = getTargetEmps();
    if (!targetEmps.length) { alert("Please select at least one employee."); return; }
    const keys = mode === "year" ? yearKeys : [...selectedMonths].sort();
    if (!keys.length) { alert("No data found for the selected period."); return; }
    const rows = buildExportRows(targetEmps, keys, fxRates);
    if (!rows.length) { alert("No payroll data found for the selected filters."); return; }
    const label = mode === "year" ? `${selectedYear}` : keys.map(k => { const [y,m] = k.split("-"); return `${MONTHS_SHORT[parseInt(m)-1]}${y}`; }).join("-");
    const empLabel = targetEmps.length === employees.length ? "AllEmployees" : targetEmps.map(e => (e.name || "Employee").replace(/\s+/g,"_")).join("-");
    exportToCSV(rows, `EasySkill_PNG_Tax_${empLabel}_${label}.csv`);
  };

  const [pdfExporting, setPdfExporting] = useState(false);

  const doExportPDF = async () => {
    const targetEmps = getTargetEmps();
    if (!targetEmps.length) { alert("Please select at least one employee."); return; }
    const keys = mode === "year" ? yearKeys : [...selectedMonths].sort();
    if (!keys.length) { alert("No data found for the selected period."); return; }

    setPdfExporting(true);
    try {
      const zip = new JSZip();
      let count = 0;

      for (const emp of targetEmps) {
        for (const key of keys) {
          const d = emp.monthlyData?.[key];
          if (!d?.result) continue;

          const [yr, mo] = key.split("-");
          const monthIdx = parseInt(mo) - 1;
          let ytdGross = 0, ytdTax = 0;
          for (let m = 0; m < monthIdx; m++) {
            const k = `${yr}-${String(m + 1).padStart(2, "0")}`;
            const md = emp.monthlyData?.[k];
            if (md?.result) { ytdGross += md.result.monthlyGross || 0; ytdTax += md.result.monthlyTax || 0; }
          }

          const html = buildPayslipHTML(emp, key, { rateLines: d.rateLines || [], allowances: d.allowances || [], result: d.result }, ytdGross, ytdTax);
          const monthName = MONTHS[monthIdx];
          const empName = (emp.name || "Employee").replace(/\s+/g, "_");
          const filename = `payslip_${empName}_${monthName}_${yr}.pdf`;

          const container = document.createElement("div");
          container.innerHTML = html;
          document.body.appendChild(container);

          const pdfBlob = await html2pdf().set({
            margin: 0,
            filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          }).from(container).outputPdf("blob");

          document.body.removeChild(container);
          zip.file(filename, pdfBlob);
          count++;

          // Singapore payslip for split contracts
          if (emp.splitContract && d.result.isSplit && d.result.sgTotalNative > 0) {
            let ytdSg = 0;
            for (let m = 0; m < monthIdx; m++) {
              const km = `${yr}-${String(m + 1).padStart(2, "0")}`;
              const mdm = emp.monthlyData?.[km];
              if (mdm?.result?.sgTotalPGK) ytdSg += mdm.result.sgTotalPGK;
            }
            const sgHtml = buildSingaporePayslipHTML(emp, key, { rateLines: d.rateLines || [], allowances: d.allowances || [], result: d.result }, ytdSg);
            const sgFilename = `payslip_SG_${empName}_${monthName}_${yr}.pdf`;
            const sgContainer = document.createElement("div");
            sgContainer.innerHTML = sgHtml;
            document.body.appendChild(sgContainer);
            const sgBlob = await html2pdf().set({
              margin: 0, filename: sgFilename,
              image: { type: "jpeg", quality: 0.98 },
              html2canvas: { scale: 2, useCORS: true },
              jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            }).from(sgContainer).outputPdf("blob");
            document.body.removeChild(sgContainer);
            zip.file(sgFilename, sgBlob);
            count++;
          }
        }
      }

      if (count === 0) { alert("No payroll data found for the selected filters."); return; }

      if (count === 1) {
        const file = Object.values(zip.files)[0];
        const blob = await file.async("blob");
        saveAs(blob, file.name);
      } else {
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const label = mode === "year" ? `${selectedYear}` : keys.map(k => { const [y,m] = k.split("-"); return `${MONTHS_SHORT[parseInt(m)-1]}${y}`; }).join("-");
        saveAs(zipBlob, `payslips_${label}.zip`);
      }
    } finally {
      setPdfExporting(false);
    }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#2a3040", border:"1px solid rgba(255,197,67,0.35)", borderRadius:14, padding:28, width:"100%", maxWidth:560 }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div style={{ color:"#ffc543", fontWeight:700, fontSize:16 }}>📊 Export Tax Declaration</div>
          <button style={S.btnRed} onClick={onClose}>✕</button>
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={S.label}>Employees to export</label>
          <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"8px 12px", maxHeight:160, overflowY:"auto" }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", padding:"4px 0", borderBottom:"1px solid rgba(255,255,255,0.06)", marginBottom:4 }}>
              <input type="checkbox" checked={selectedEmpIds.size === employees.length} onChange={toggleAllEmps} style={{ accentColor:"#ffc543" }} />
              <span style={{ color:"#ffc543", fontWeight:600, fontSize:12 }}>Select all ({employees.length})</span>
            </label>
            {employees.map(e => (
              <label key={e.id} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", padding:"3px 0" }}>
                <input type="checkbox" checked={selectedEmpIds.has(String(e.id))} onChange={() => toggleEmp(String(e.id))} style={{ accentColor:"#31b4a6" }} />
                <span style={{ color:"#fff", fontSize:12 }}>{e.name || "Unnamed"}</span>
                <span style={{ color:"#75788b", fontSize:10, marginLeft:"auto" }}>{normalizeResidency(e.empType) === "resident" ? "Resident" : "Non-Resident"}</span>
              </label>
            ))}
          </div>
          <div style={{ fontSize:10, color:"#75788b", marginTop:4 }}>{selectedEmpIds.size} employee{selectedEmpIds.size !== 1 ? "s" : ""} selected</div>
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={S.label}>Period</label>
          <div style={{ display:"flex", gap:8 }}>
            {[{v:"months",l:"Select months"},{v:"year",l:"Full year"}].map(({v,l}) => (
              <button key={v} onClick={() => setMode(v)} style={{
                flex:1, padding:"9px 0", borderRadius:7, border:"none", cursor:"pointer", fontWeight:600, fontSize:13,
                background: mode===v ? "linear-gradient(135deg,#ffc543,#e5a520)" : "rgba(255,255,255,0.06)",
                color: mode===v ? "#212737" : "#75788b",
              }}>{l}</button>
            ))}
          </div>
        </div>

        {mode === "year" && (
          <div style={{ marginBottom:18 }}>
            <label style={S.label}>Year</label>
            <select style={{ ...S.select, width:"100%" }} value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
              {[currentYear-1, currentYear, currentYear+1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <div style={{ marginTop:8, fontSize:11, color:"#75788b" }}>
              {yearKeys.length} month(s) with data found for {selectedYear}
            </div>
          </div>
        )}

        {mode === "months" && (
          <div style={{ marginBottom:18 }}>
            <label style={S.label}>Select months (only months with data shown)</label>
            {sortedKeys.length === 0
              ? <div style={{ color:"#75788b", fontSize:12 }}>No payroll data found yet.</div>
              : <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:6 }}>
                  {sortedKeys.map(key => {
                    const [y, m] = key.split("-");
                    const label = `${MONTHS_SHORT[parseInt(m)-1]} ${y}`;
                    const on = selectedMonths.has(key);
                    return (
                      <button key={key} onClick={() => toggleMonth(key)} style={{
                        padding:"6px 12px", borderRadius:6, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
                        background: on ? "linear-gradient(135deg,#31b4a6,#259088)" : "rgba(255,255,255,0.06)",
                        color: on ? "#fff" : "#75788b",
                      }}>{label}</button>
                    );
                  })}
                </div>
            }
          </div>
        )}

        <div style={{ marginBottom:18, padding:"10px 14px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, fontSize:12, color:"#75788b" }}>
          Output: <strong style={{color:"#ffffff"}}>CSV file</strong> (opens in Excel)<br/>
          <span style={{color:"#75788b"}}>Columns per employee:</span> Name · Type · Project · Month · <strong style={{color:"#ffc543"}}>Pay Currency · FX Rate</strong> · Days · Net (currency) · <strong style={{color:"#31b4a6"}}>Net/Gross/Tax/Allowances/Total in PGK + currency</strong> · Eff. Rate
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button style={{ ...S.btnYellow, flex:1, padding:"12px 0", fontSize:14 }} onClick={doExport}>
            ⬇ Export CSV
          </button>
          <button style={{ ...S.btnTeal, flex:1, padding:"12px 0", fontSize:14, opacity: pdfExporting ? 0.6 : 1 }} onClick={doExportPDF} disabled={pdfExporting}>
            {pdfExporting ? "⏳ Generating..." : "📄 Export Payslips PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FX Rates Panel ────────────────────────────────────────────────────────────
function FxRatesPanel({ fxRates, onChange }) {
  const [open, setOpen] = useState(false);
  const currentYear = now.getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];
  const [viewYear, setViewYear] = useState(currentYear);

  const setRate = (monthKey, ccy, val) => {
    onChange({
      ...fxRates,
      [monthKey]: { ...(fxRates[monthKey] || {}), [ccy]: val }
    });
  };

  const foreignCcys = CURRENCIES.filter(c => c !== "PGK");

  return (
    <div style={{ margin:"0 36px 20px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,197,67,0.2)", borderRadius:10 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", cursor:"pointer" }}
      >
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ color:"#ffc543", fontWeight:700, fontSize:13 }}>🔄 FX Reference Rates</span>
          <span style={{ color:"#75788b", fontSize:11 }}>Monthly exchange rates used for all employees · PGK per 1 unit of foreign currency</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {foreignCcys.map(ccy => {
            const lastKey = MONTHS_SHORT.map((_,i) => `${viewYear}-${String(i+1).padStart(2,"0")}`).reverse().find(k => fxRates[k]?.[ccy]);
            const rate = lastKey ? fxRates[lastKey]?.[ccy] : null;
            return rate ? (
              <span key={ccy} style={{ fontFamily:"'Poppins',sans-serif", color:"#ffc543", fontSize:11, background:"rgba(255,197,67,0.08)", border:"1px solid rgba(255,197,67,0.2)", borderRadius:4, padding:"2px 8px" }}>
                1 {ccy} = {rate} PGK
              </span>
            ) : null;
          })}
          <span style={{ color:"#75788b", fontSize:13 }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", padding:"16px 18px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
            <span style={{ color:"#75788b", fontSize:11 }}>Year:</span>
            {years.map(y => (
              <button key={y} onClick={() => setViewYear(y)} style={{
                padding:"4px 14px", borderRadius:6, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
                background: viewYear===y ? "linear-gradient(135deg,#ffc543,#e5a520)" : "rgba(255,255,255,0.06)",
                color: viewYear===y ? "#212737" : "#75788b",
              }}>{y}</button>
            ))}
          </div>

          <div style={{ overflowX:"auto" }}>
            <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12 }}>
              <thead>
                <tr>
                  <th style={{ color:"#75788b", fontWeight:600, fontSize:10, letterSpacing:"0.1em", textTransform:"uppercase", padding:"6px 12px", textAlign:"left", width:80 }}>Month</th>
                  {foreignCcys.map(ccy => (
                    <th key={ccy} style={{ color:"#ffc543", fontWeight:700, fontSize:11, padding:"6px 16px", textAlign:"center", minWidth:160 }}>
                      1 {ccy} → PGK
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MONTHS_SHORT.map((m, i) => {
                  const key = `${viewYear}-${String(i+1).padStart(2,"0")}`;
                  const isCurrentMonth = viewYear === now.getFullYear() && i === now.getMonth();
                  return (
                    <tr key={key} style={{ background: isCurrentMonth ? "rgba(49,180,166,0.05)" : i%2===0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                      <td style={{ padding:"7px 12px", color: isCurrentMonth ? "#31b4a6" : "#777", fontWeight: isCurrentMonth ? 700 : 400 }}>
                        {m} {viewYear}
                      </td>
                      {foreignCcys.map(ccy => (
                        <td key={ccy} style={{ padding:"5px 12px", textAlign:"center" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}>
                            <input
                              type="number"
                              min={0}
                              step="0.0001"
                              placeholder="—"
                              value={fxRates[key]?.[ccy] ?? ""}
                              onChange={e => setRate(key, ccy, e.target.value)}
                              style={{
                                ...S.input,
                                width:100,
                                textAlign:"right",
                                fontFamily:"'Poppins',sans-serif",
                                fontSize:12,
                                color: fxRates[key]?.[ccy] ? "#e8e0d0" : "#444",
                              }}
                            />
                            <span style={{ color:"#384460", fontSize:11 }}>PGK</span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:10, fontSize:10, color:"#384460" }}>
            💡 These rates are used automatically when calculating months for employees paid in USD or AUD. Leave blank to enter manually per month.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  const savedLocal = useRef(null);
  if (savedLocal.current === null) {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      savedLocal.current = raw ? JSON.parse(raw) : {};
    } catch { savedLocal.current = {}; }
  }

  const [employees, setEmployees] = useState(
    savedLocal.current.employees?.length ? savedLocal.current.employees : [newEmployee()]
  );
  const [fxRates, setFxRates] = useState(savedLocal.current.fxRates || {});
  const [showExport, setShowExport] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [authorizedUsers, setAuthorizedUsers] = useState(
    savedLocal.current.authorizedUsers?.length ? savedLocal.current.authorizedUsers : DEFAULT_ADMINS.map(e => ({ email: e, role: "admin" }))
  );
  const [accessGranted, setAccessGranted] = useState(false);
  const [showUserAdmin, setShowUserAdmin] = useState(false);
  const drive = useFirebase();
  const saveTimerRef = useRef(null);
  // State (not ref) so that flipping to true triggers the auto-save effect.
  const [firestoreReady, setFirestoreReady] = useState(false);

  const currentEmail = drive.user?.email?.toLowerCase();
  const currentUserEntry = authorizedUsers.find(u => u.email === currentEmail);
  const isAdmin = currentUserEntry?.role === "admin" || DEFAULT_ADMINS.includes(currentEmail);

  const getData = useCallback(() => ({ employees, fxRates, authorizedUsers, savedAt: new Date().toISOString() }), [employees, fxRates, authorizedUsers]);

  useEffect(() => {
    try { localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(getData())); } catch {}
  }, [getData]);

  useEffect(() => {
    if (!drive.isSignedIn || !accessGranted || !firestoreReady) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      drive.save(getData()).then(() => {
        setLastSaved(new Date().toLocaleTimeString());
      });
    }, 3000);
    return () => clearTimeout(saveTimerRef.current);
  }, [employees, fxRates, authorizedUsers, drive.isSignedIn, accessGranted, firestoreReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply data loaded from Firestore — fires on both fresh sign-in and session restore.
  // Compares Firestore vs localStorage by counting saved months: the richer dataset wins.
  // This protects a user whose localStorage is ahead of Firestore (e.g. Gee after Pierre
  // had been overwriting Firestore with his stale data).
  useEffect(() => {
    if (!drive.loadedData) return;
    const firestoreData = drive.loadedData;

    const countSavedMonths = (emps) =>
      (emps || []).reduce((acc, emp) =>
        acc + Object.values(emp.monthlyData || {}).filter(m => m?.result).length, 0);

    const firestoreMonths = countSavedMonths(firestoreData?.employees);
    const localMonths = countSavedMonths(employees);

    if (firestoreMonths >= localMonths) {
      // Firestore has equal or more data — it is the reference, apply it
      if (firestoreData?.authorizedUsers?.length) setAuthorizedUsers(firestoreData.authorizedUsers);
      if (firestoreData?.employees?.length) {
        setEmployees(firestoreData.employees);
        setFxRates(firestoreData.fxRates || {});
        setLastSaved("loaded from Drive");
      }
    }
    // Unlock saves — using setState triggers the auto-save effect to fire immediately
    setFirestoreReady(true);
  }, [drive.loadedData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignIn = () => drive.signIn();

  useEffect(() => {
    if (!drive.isSignedIn || !currentEmail) { setAccessGranted(false); return; }
    const isAuthorized = authorizedUsers.some(u => u.email === currentEmail) || DEFAULT_ADMINS.includes(currentEmail) || AUTHORIZED_EMAILS.includes(currentEmail);
    setAccessGranted(isAuthorized);
  }, [drive.isSignedIn, currentEmail, authorizedUsers]);

  const handleSignOut = () => {
    drive.signOut();
    setAccessGranted(false);
    setFirestoreReady(false);
  };

  const handleSave = () => {
    drive.save(getData()).then(() => setLastSaved(new Date().toLocaleTimeString()));
  };

  const [showArchived, setShowArchived] = useState(false);
  const upd = (emp) => setEmployees(p => p.map(e => e.id===emp.id ? emp : e));
  const del = (id)  => setEmployees(p => p.filter(e => e.id!==id));
  const archive = (id) => setEmployees(p => p.map(e => e.id===id ? {...e, archived:true} : e));
  const unarchive = (id) => setEmployees(p => p.map(e => e.id===id ? {...e, archived:false} : e));
  const add = ()    => setEmployees(p => [...p, newEmployee()]);
  const activeEmployees = employees.filter(e => !e.archived);
  const archivedEmployees = employees.filter(e => e.archived);
  const visibleEmployees = showArchived ? employees : activeEmployees;

  if (drive.isSignedIn && currentEmail && !accessGranted) {
    return <AccessDenied email={currentEmail} onSignOut={handleSignOut} />;
  }

  if (drive.status === "checking") {
    return (
      <div style={{ minHeight:"100vh", background:"#212737", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Poppins','Segoe UI',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Raleway:wght@400;600;700;800&display=swap" rel="stylesheet"/>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontFamily:"'Raleway',sans-serif", fontSize:32, fontWeight:800, color:"#ffc543", marginBottom:4 }}>easy skill</div>
          <div style={{ fontSize:14, color:"#75788b" }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!drive.isSignedIn) {
    return (
      <div style={{ minHeight:"100vh", background:"#212737", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Poppins','Segoe UI',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Raleway:wght@400;600;700;800&display=swap" rel="stylesheet"/>
        <div style={{ textAlign:"center", maxWidth:420, padding:40 }}>
          <div style={{ fontFamily:"'Raleway',sans-serif", fontSize:32, fontWeight:800, color:"#ffc543", marginBottom:4 }}>easy skill</div>
          <div style={{ fontSize:14, color:"#75788b", marginBottom:6 }}>PNG Payroll</div>
          <div style={{ fontSize:11, color:"#384460", marginBottom:36, letterSpacing:"0.08em", textTransform:"uppercase" }}>Internal Tool — Restricted Access</div>
          <button
            onClick={handleSignIn}
            disabled={drive.status === "signing-in"}
            style={{
              background:"#fff", color:"#384460", border:"1px solid #ddd", borderRadius:8,
              padding:"12px 28px", cursor:"pointer", fontSize:14, fontWeight:600,
              display:"inline-flex", alignItems:"center", gap:10, opacity: drive.status === "signing-in" ? 0.6 : 1,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {drive.status === "signing-in" ? "Signing in..." : "Sign in with Google"}
          </button>
          {drive.error && <div style={{ color:"#e08070", fontSize:12, marginTop:16 }}>{drive.error}</div>}
          <div style={{ color:"#384460", fontSize:10, marginTop:32, lineHeight:1.6 }}>
            Access restricted to authorised Easy Skill personnel.<br/>
            Contact your administrator for access.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"#212737", color:"#ffffff", fontFamily:"'Poppins','Segoe UI',sans-serif", paddingBottom:60 }}>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Raleway:wght@400;600;700;800&display=swap" rel="stylesheet"/>

      {/* Header */}
      <div style={{ borderBottom:"1px solid rgba(255,197,67,0.2)", padding:"20px 36px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
            <span style={{ fontFamily:"'Raleway',sans-serif", fontSize:20, fontWeight:800, color:"#ffc543" }}>PNG Tax</span>
            <span style={{ fontSize:12, color:"#75788b", letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600 }}>Back-Calculator</span>
          </div>
          <div style={{ fontSize:10, color:"#75788b", marginTop:2 }}>Easy Skill · Papua New Guinea Salary Tax Engine · Internal Tool</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <DriveStatusBar drive={drive} onSignIn={handleSignIn} onSave={handleSave} onSignOut={handleSignOut} lastSaved={lastSaved} />
          {isAdmin && <button style={{ ...S.btnGhost, fontSize:11, padding:"7px 14px" }} onClick={() => setShowUserAdmin(true)}>👥 Users</button>}
          <button style={S.btnYellow} onClick={() => setShowExport(true)}>📊 Export</button>
        </div>
      </div>

      {/* FX Rates master table */}
      <div style={{ padding:"0 0 4px" }}>
        <FxRatesPanel fxRates={fxRates} onChange={setFxRates} />
      </div>

      {/* Employees */}
      <div style={{ padding:"4px 36px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ color:"#75788b", fontSize:12 }}>{activeEmployees.length} active{archivedEmployees.length > 0 ? ` · ${archivedEmployees.length} archived` : ""}</div>
            {archivedEmployees.length > 0 && (
              <button
                style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", background: showArchived ? "rgba(246,134,68,0.12)" : undefined, color: showArchived ? "#f68644" : "#75788b", borderColor: showArchived ? "rgba(246,134,68,0.3)" : undefined }}
                onClick={() => setShowArchived(v => !v)}
              >
                {showArchived ? "Hide archived" : "Show archived"}
              </button>
            )}
          </div>
          <button style={S.btnYellow} onClick={add}>+ Add Employee</button>
        </div>
        {visibleEmployees.map(e => <EmployeeCard key={e.id} employee={e} onUpdate={upd} onDelete={del} onArchive={e.archived ? () => unarchive(e.id) : () => archive(e.id)} fxRates={fxRates} />)}
      </div>

      {showExport && <ExportModal employees={employees} fxRates={fxRates} onClose={() => setShowExport(false)} />}
      {showUserAdmin && <UserAdminPanel users={authorizedUsers} onUpdate={setAuthorizedUsers} onClose={() => setShowUserAdmin(false)} />}

      {/* Tax reference */}
      <div style={{ margin:"0 36px", display:"flex", gap:14, flexWrap:"wrap" }}>
        {[
          { title:"Non-Resident", note:"22% from first kina", brackets:NON_RESIDENT_BRACKETS },
          { title:"Resident",     note:"K20,000 tax-free · Rebate: K450 (1st dep) + K300 (2nd) + K300 (3rd), max 3", brackets:RESIDENT_BRACKETS },
        ].map(({title,note,brackets}) => (
          <div key={title} style={{ flex:1, minWidth:250, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:14 }}>
            <div style={{ color:"#ffc543", fontWeight:700, fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>{title}</div>
            <div style={{ color:"#384460", fontSize:10, marginBottom:8 }}>{note}</div>
            {brackets.map((b,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"3px 0", fontFamily:"'Poppins',sans-serif", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                <span style={{color:"#75788b"}}>{b.from.toLocaleString()} – {b.to===Infinity?"∞":b.to.toLocaleString()}</span>
                <span style={{color:"#ffc543"}}>{(b.rate*100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
